/**
 * Single source of truth for artist-facing strings and external URLs.
 *
 * Filling in any of the `links` fields is enough to light up the Listen /
 * Stream CTAs across the menu, victory, game-over, and in-game HUD — every
 * surface reads through `primaryListenLink()` and hides itself when the
 * result is null. Ship with blanks; flip the switch later by editing this
 * file alone.
 */

export interface BrandLinks {
  spotify?: string;
  appleMusic?: string;
  amazonMusic?: string;
  youtubeMusic?: string;
  youtube?: string;
  /** Linkfire / Show.co / Linktree smart link. Wins over individual DSPs. */
  smartLink?: string;
  instagram?: string;
  tiktok?: string;
  merch?: string;
  tickets?: string;
}

export interface DspIcon {
  key: "spotify" | "appleMusic" | "amazonMusic" | "youtubeMusic" | "instagram";
  label: string;
  url: string;
}

export interface TrackMeta {
  title: string;
  artist: string;
}

export const BRAND = {
  artist: "Nazareth Suedegod",
  artistHtml: "Nazareth<br/>Suedegod",
  tagline: "LA → K-town",
  track: { title: "K-Town Run", artist: "Nazareth Suedegod" } as TrackMeta,
  links: {
    spotify: "https://open.spotify.com/track/00b2JlRGUQstovzxqJxOVt?si=C_aK3EvxRVqW8TvekgZSyg",
    appleMusic: "https://music.apple.com/us/album/evol-suede/1895876674?i=6764680516",
    amazonMusic:
      "https://amazon.com/music/player/tracks/B0GZ14YQ8S?marketplaceId=ATVPDKIKX0DER&musicTerritory=US&ref=dm_sh_FvJdJlEfHn3T6CPLJDLqWlYzm",
    youtubeMusic: "https://music.youtube.com/watch?v=xAS7xA8akJ4&si=dOj9ndsElL20OsKv",
    youtube: "",
    smartLink: "",
    instagram:
      "https://www.instagram.com/suedegod_?igsh=NTc4MTIwNjQ2YQ%3D%3D&utm_source=qr",
    tiktok: "",
    merch: "",
    tickets: "",
  } as BrandLinks,
  shareHashtags: ["#NazarethSuedegod", "#KTownRun"],
} as const;

export function hasLink(u: string | undefined): u is string {
  return typeof u === "string" && u.trim().length > 0;
}

/**
 * Best stream URL to send a player to. Smart link wins (one URL routes to
 * each player's preferred DSP); falls back to individual DSP URLs in
 * descending order of artist preference.
 */
export function primaryListenLink(): string | null {
  const l = BRAND.links;
  if (hasLink(l.smartLink)) return l.smartLink;
  if (hasLink(l.spotify)) return l.spotify;
  if (hasLink(l.appleMusic)) return l.appleMusic;
  if (hasLink(l.amazonMusic)) return l.amazonMusic;
  if (hasLink(l.youtubeMusic)) return l.youtubeMusic;
  if (hasLink(l.youtube)) return l.youtube;
  return null;
}

/**
 * Returns the populated per-DSP list (in display order) for the icon row.
 * Empty when no DSP URLs are configured — callers should not render the
 * row at all in that case.
 */
export function dspIcons(): DspIcon[] {
  const l = BRAND.links;
  const out: DspIcon[] = [];
  if (hasLink(l.spotify)) out.push({ key: "spotify", label: "Spotify", url: l.spotify });
  if (hasLink(l.appleMusic)) out.push({ key: "appleMusic", label: "Apple Music", url: l.appleMusic });
  if (hasLink(l.amazonMusic)) out.push({ key: "amazonMusic", label: "Amazon Music", url: l.amazonMusic });
  if (hasLink(l.youtubeMusic)) out.push({ key: "youtubeMusic", label: "YouTube Music", url: l.youtubeMusic });
  if (hasLink(l.instagram)) out.push({ key: "instagram", label: "Instagram", url: l.instagram });
  return out;
}

export type Tier = "Rookie" | "Bronze" | "Silver" | "Gold" | "Suedegod Fan";

export function tierFor(runs: number): Tier {
  if (runs >= 100) return "Suedegod Fan";
  if (runs >= 40) return "Gold";
  if (runs >= 15) return "Silver";
  if (runs >= 5) return "Bronze";
  return "Rookie";
}
